import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

/**
 * App-level auth wiring:
 *  - hydrates the session on load (validates the stored token via /auth/me),
 *  - reacts to the events api.ts emits during transparent token refresh,
 *  - surfaces the login modal when a session expires.
 * Mounted once near the app root.
 */
export function useAuthBootstrap() {
  useEffect(() => {
    // Validate / hydrate any persisted session.
    void useAuthStore.getState().initialize();

    // Refresh succeeded inside api.ts: pull the rotated tokens into the store.
    const onTokenRefreshed = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        access_token?: string;
        refresh_token?: string;
      };
      useAuthStore.setState({
        accessToken: detail?.access_token ?? useAuthStore.getState().accessToken,
        refreshToken: detail?.refresh_token ?? useAuthStore.getState().refreshToken,
      });
    };

    // Refresh failed / no valid session: clear state and prompt for login.
    const onSessionExpired = () => {
      const hadUser = !!useAuthStore.getState().user;
      useAuthStore.getState().handleSessionExpired();
      useUIStore.getState().closePanel('accountPanel');
      if (hadUser) {
        useUIStore.getState().openAuthModal('login');
        useUIStore.getState().addNotification({
          type: 'warning',
          message: 'Your session expired. Please sign in again.',
        });
      }
    };

    // Backwards-compatible alias for the previously dispatched event name.
    const onLegacyLogout = () => onSessionExpired();

    window.addEventListener('auth:token-refreshed', onTokenRefreshed);
    window.addEventListener('auth:session-expired', onSessionExpired);
    window.addEventListener('auth:logout', onLegacyLogout);

    return () => {
      window.removeEventListener('auth:token-refreshed', onTokenRefreshed);
      window.removeEventListener('auth:session-expired', onSessionExpired);
      window.removeEventListener('auth:logout', onLegacyLogout);
    };
  }, []);
}
